import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { authService } from '../services/supabaseService';
import type { User as SupabaseUser } from '@supabase/supabase-js';

interface User {
  id: string;
  email: string;
  name: string;
  subscription: 'free' | 'premium';
  weekly_time_allocation: number;
  age?: number | null;
  annual_income?: number | null;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    // Check initial session
    const checkSession = async () => {
      try {
        console.log('AuthContext: Checking initial session...');
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          console.log('AuthContext: Session found, fetching profile...');
          try {
            const profile = await authService.getCurrentUser();
            console.log('AuthContext: Profile loaded:', profile);
            setUser(profile);
          } catch (profileError) {
            console.error('AuthContext: Profile error:', profileError);
            setUser(null);
          }
        } else {
          console.log('AuthContext: No session found');
          setUser(null);
        }
      } catch (error) {
        console.error('AuthContext: Session error:', error);
        setUser(null);
      } finally {
        console.log('AuthContext: Initial check complete');
        setLoading(false);
      }
    };

    checkSession();

    // Set a timeout to ensure loading doesn't get stuck
    timeoutId = setTimeout(() => {
      console.log('AuthContext: Loading timeout reached, forcing loading = false');
      setLoading(false);
    }, 5000);

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('AuthContext: Auth state change:', event, !!session);
        
        // Clear the timeout since we got a response
        clearTimeout(timeoutId);
        
        if (event === 'SIGNED_OUT' || !session) {
          setUser(null);
          setLoading(false);
        } else if (event === 'SIGNED_IN' && session?.user) {
          // Don't fetch profile here, let login/register handle it
          setLoading(false);
        } else if (event === 'TOKEN_REFRESHED' && session?.user && user) {
          // Keep existing user data on token refresh
          setLoading(false);
        } else {
          setLoading(false);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      console.log('AuthContext: Starting login...');
      
      const result = await authService.signIn(email, password);
      
      if (result.user) {
        console.log('AuthContext: Login successful, fetching profile...');
        try {
          const profile = await authService.getCurrentUser();
          console.log('AuthContext: Profile fetched:', profile);
          setUser(profile);
        } catch (profileError) {
          console.error('AuthContext: Error fetching profile after login:', profileError);
          throw new Error('Login successful but could not load profile. Please refresh the page.');
        }
      }
    } catch (error: any) {
      console.error('AuthContext: Login error:', error);
      throw new Error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const register = async (name: string, email: string, password: string) => {
    try {
      setLoading(true);
      console.log('AuthContext: Starting registration...');
      
      await authService.signUp(email, password, name);
      
      // Don't log the user in or fetch profile - they need to confirm email first
      console.log('AuthContext: Registration successful, awaiting email confirmation...');
      
    } catch (error: any) {
      console.error('AuthContext: Registration error:', error);
      throw new Error(error.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      console.log('AuthContext: Logging out...');
      await authService.signOut();
      setUser(null);
    } catch (error) {
      console.error('AuthContext: Error signing out:', error);
    }
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 